import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InputFieldTime, MultiSelectButtonGroup } from 'qampo-ui-library';
import { Dropdown, DropdownOptions, InputField, InputFieldNumber } from '../UI';
import { DateField, Toggle } from '../UI/base';
import type {
  RecurrenceDaysOfWeekEnum,
  ReservedStaffTimeRequest,
  ReservedStaffTimeResponse,
  StaffResponse
} from '../../api/generated';
import {
  BookingFormModalShell,
  DEFAULT_INTERVAL_WEEKS,
  WEEKDAYS_IN_ORDER
} from '../Bookings/shared';
import {
  buildFlexiblePlacement,
  formatDurationHours,
  minutesBetween,
  splitFlexiblePlacement
} from './reservedStaffTimeBooking';
import {
  DEFAULT_DURATION_MINUTES,
  RESERVED_STAFF_TIME_TYPE,
  WORK_WEEK_DAYS
} from './reservedStaffTimeBookingConstants';
import styles from './ReservedStaffTimeBookingFormModal.module.css';

const KEY_PREFIX = 'reserved-staff-time';

interface ReservedStaffTimeBookingFormModalProps {
  booking: ReservedStaffTimeResponse | null;
  staff: StaffResponse[];
  onSave: (payload: ReservedStaffTimeRequest) => void;
  onClose: () => void;
}

interface ReservedStaffTimeFormErrors {
  staff?: string;
  duration?: string;
  from?: string;
  to?: string;
  days?: string;
  interval?: string;
  time?: string;
}

interface InitialFormState {
  durationMinutes: number;
  windowStart: string;
  windowEnd: string;
  isRecurring: boolean;
  selectedDays: RecurrenceDaysOfWeekEnum[];
  intervalWeeks: number;
  isTimeRestricted: boolean;
  timeOfDayStart: string;
  timeOfDayEnd: string;
}

// The recurrence kind (one-off vs recurring) is fixed once at creation, so on edit the placement is
// loaded from its stored shape and the Repeat switch only renders when creating — mirroring the
// other booking forms.
const buildInitialState = (booking: ReservedStaffTimeResponse | null): InitialFormState => {
  if (booking) {
    const placement = splitFlexiblePlacement(booking.placement);
    return {
      durationMinutes: booking.durationMinutes,
      windowStart: placement.windowStart,
      windowEnd: placement.windowEnd,
      isRecurring: placement.isRecurring,
      selectedDays: placement.daysOfWeek,
      intervalWeeks: placement.intervalWeeks,
      isTimeRestricted: placement.isTimeRestricted,
      timeOfDayStart: placement.timeOfDayStart,
      timeOfDayEnd: placement.timeOfDayEnd
    };
  }
  return {
    durationMinutes: DEFAULT_DURATION_MINUTES,
    windowStart: '',
    windowEnd: '',
    isRecurring: false,
    selectedDays: [],
    intervalWeeks: DEFAULT_INTERVAL_WEEKS,
    isTimeRestricted: false,
    timeOfDayStart: '',
    timeOfDayEnd: ''
  };
};

export const ReservedStaffTimeBookingFormModal = ({
  booking,
  staff,
  onSave,
  onClose
}: ReservedStaffTimeBookingFormModalProps) => {
  const { t } = useTranslation();
  const initial = buildInitialState(booking);
  const [staffId, setStaffId] = useState<string | null>(booking?.staffId ?? null);
  const [durationMinutes, setDurationMinutes] = useState(initial.durationMinutes);
  const [windowStart, setWindowStart] = useState(initial.windowStart);
  const [windowEnd, setWindowEnd] = useState(initial.windowEnd);
  const [isRecurring, setIsRecurring] = useState(initial.isRecurring);
  const [selectedDays, setSelectedDays] = useState<RecurrenceDaysOfWeekEnum[]>(initial.selectedDays);
  const [intervalWeeks, setIntervalWeeks] = useState(initial.intervalWeeks);
  const [isTimeRestricted, setIsTimeRestricted] = useState(initial.isTimeRestricted);
  const [timeOfDayStart, setTimeOfDayStart] = useState(initial.timeOfDayStart);
  const [timeOfDayEnd, setTimeOfDayEnd] = useState(initial.timeOfDayEnd);
  const [reason, setReason] = useState(booking?.reason ?? '');
  const [errors, setErrors] = useState<ReservedStaffTimeFormErrors>({});

  const selectedStaff = staff.find((member) => member.id === staffId) ?? null;
  const isCreating = booking === null;
  const durationHint =
    durationMinutes > 0
      ? t('reserved-staff-time-duration-hint', { hours: formatDurationHours(durationMinutes) })
      : undefined;

  const dayOptions = WEEKDAYS_IN_ORDER.map((option, index) => ({
    id: index,
    value: option.day,
    label: t(option.shortLabelKey)
  }));
  const selectedDayOptions = dayOptions.filter((option) => selectedDays.includes(option.value));

  const handleToggleRecurring = () => {
    const next = !isRecurring;
    if (next && selectedDays.length === 0) setSelectedDays([...WORK_WEEK_DAYS]);
    setIsRecurring(next);
  };

  const validateTimeOfDay = (next: ReservedStaffTimeFormErrors): void => {
    if (!timeOfDayStart || !timeOfDayEnd) {
      next.time = t('reserved-staff-time-error-time-required');
      return;
    }
    const windowMinutes = minutesBetween(timeOfDayStart, timeOfDayEnd);
    if (windowMinutes === null || windowMinutes <= 0) {
      next.time = t('reserved-staff-time-error-time-range');
    } else if (durationMinutes > windowMinutes) {
      next.time = t('reserved-staff-time-error-duration-window');
    }
  };

  const validate = (): boolean => {
    const next: ReservedStaffTimeFormErrors = {};
    if (!staffId) next.staff = t('reserved-staff-time-error-staff');
    if (durationMinutes <= 0) next.duration = t('reserved-staff-time-error-duration');
    if (!windowStart) next.from = t('reserved-staff-time-error-from');
    if (!windowEnd) {
      next.to = t('reserved-staff-time-error-to');
    } else if (windowStart && windowEnd < windowStart) {
      next.to = t('reserved-staff-time-error-to-range');
    }
    if (isRecurring) {
      if (selectedDays.length === 0) next.days = t('reserved-staff-time-error-days');
      if (intervalWeeks < DEFAULT_INTERVAL_WEEKS) {
        next.interval = t('reserved-staff-time-error-interval');
      }
    }
    if (isTimeRestricted) validateTimeOfDay(next);
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = () => {
    if (!validate() || !staffId) return;
    onSave({
      type: RESERVED_STAFF_TIME_TYPE,
      reason: reason.trim() || undefined,
      staffId,
      durationMinutes,
      placement: buildFlexiblePlacement({
        windowStart,
        windowEnd,
        isRecurring,
        daysOfWeek: selectedDays,
        intervalWeeks,
        timeOfDayWindow: isTimeRestricted
          ? { start: timeOfDayStart, end: timeOfDayEnd }
          : undefined
      })
    });
  };

  return (
    <BookingFormModalShell
      keyPrefix={KEY_PREFIX}
      isEditing={booking !== null}
      onSave={handleSave}
      onClose={onClose}
    >
      <Dropdown
        label={t('reserved-staff-time-staff-label')}
        required
        placeholder={t('reserved-staff-time-staff-placeholder')}
        error={Boolean(errors.staff)}
        helperText={errors.staff}
        selectedOption={selectedStaff?.fullName}
      >
        <DropdownOptions
          options={staff}
          selectedOption={selectedStaff}
          getKey={(member) => member.id}
          displayOption={(member) => member.fullName}
          handleChange={(member) => setStaffId(member.id)}
        />
      </Dropdown>

      <InputFieldNumber
        label={t('reserved-staff-time-duration-label')}
        width="160px"
        value={durationMinutes}
        allowNegative={false}
        suffixLabel={t('room-booking-minutes-suffix')}
        error={Boolean(errors.duration)}
        helperText={errors.duration ?? durationHint}
        onChange={(e) => setDurationMinutes(Number(e.target.value) || 0)}
      />

      <div className={styles.dateRow}>
        <DateField
          label={t('reserved-staff-time-from-label')}
          required
          value={windowStart}
          error={Boolean(errors.from)}
          helperText={errors.from}
          onChange={setWindowStart}
        />
        <DateField
          label={t('reserved-staff-time-to-label')}
          required
          value={windowEnd}
          error={Boolean(errors.to)}
          helperText={errors.to}
          onChange={setWindowEnd}
        />
      </div>

      {isCreating && (
        <Toggle
          label={t('reserved-staff-time-repeat-label')}
          checked={isRecurring}
          onChange={handleToggleRecurring}
        />
      )}

      {isRecurring && (
        <div className={styles.recurrenceFields}>
          <div>
            <MultiSelectButtonGroup
              label={t('reserved-staff-time-days-label')}
              options={dayOptions}
              value={selectedDayOptions}
              onChange={(selected) =>
                setSelectedDays(
                  WEEKDAYS_IN_ORDER.map((option) => option.day).filter((day) =>
                    selected.some((item) => item.value === day)
                  )
                )
              }
            />
            {errors.days && <span className={styles.fieldError}>{errors.days}</span>}
          </div>
          <InputFieldNumber
            label={t('booking-interval-label')}
            width="140px"
            value={intervalWeeks}
            allowNegative={false}
            suffixLabel={t('booking-interval-suffix')}
            error={Boolean(errors.interval)}
            helperText={errors.interval}
            onChange={(e) => setIntervalWeeks(Number(e.target.value) || 0)}
          />
        </div>
      )}

      <Toggle
        label={t('reserved-staff-time-restrict-time-label')}
        checked={isTimeRestricted}
        onChange={() => setIsTimeRestricted((v) => !v)}
      />

      {isTimeRestricted && (
        <div>
          <div className={styles.timeRow}>
            <InputFieldTime
              label={t('booking-start-time-label')}
              width={120}
              value={timeOfDayStart}
              error={Boolean(errors.time)}
              onChange={(e) => setTimeOfDayStart(e.target.value)}
            />
            <span className={styles.timeSeparator}>&ndash;</span>
            <InputFieldTime
              label={t('booking-end-time-label')}
              width={120}
              value={timeOfDayEnd}
              error={Boolean(errors.time)}
              onChange={(e) => setTimeOfDayEnd(e.target.value)}
            />
          </div>
          {errors.time && <span className={styles.fieldError}>{errors.time}</span>}
        </div>
      )}

      <InputField
        label={t('reserved-staff-time-reason-label')}
        type="text"
        value={reason}
        placeholder={t('reserved-staff-time-reason-placeholder')}
        onChange={(e) => setReason(e.target.value)}
      />
    </BookingFormModalShell>
  );
};
